'use client';

import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { Hero as HeroType } from '@lib/types/cms/Hero';
import { toNonNullable } from '@lib/util/toNonNullable';
import { ButtonStyle, Hero as HeroUI } from 'ui-components';

type HeroProps = {
  data: HeroType;
  position: number;
};

const Hero = ({ data, position }: HeroProps) => {
  const {
    heading,
    description,
    image,
    slim,
    imageFocusArea,
    titleInternal,
    trackAsPromo,
    ctas,
  } = data || {};

  const imageUrl = image?.url;
  const imageDescription = image?.description ?? '';

  // Guard against stale Redis-cached pages transformed before `ctas` existed:
  // those cached blobs have no `ctas` key, so `ctas` can be undefined here.
  const actions = (ctas ?? []).map((cta) => ({
    label: cta.label,
    url: cta.url,
    openInNewTab: toNonNullable(cta.openInNewTab),
    style:
      cta.appearance === 'primary'
        ? ('primary' as ButtonStyle)
        : ('secondary' as ButtonStyle),
  }));

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: titleInternal || '',
      typename: 'Hero',
      position,
      creativeTitle: toNonNullable(image?.title),
      trackAsPromo: trackAsPromo ?? false,
      ctas: actions.map((action) => action.label),
    });

  if (!heading || !imageUrl) {
    return null;
  }

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      <HeroUI
        title={heading}
        image={{
          url: imageUrl,
          alt: imageDescription,
          height: toNonNullable(image.height),
          width: toNonNullable(image.width),
        }}
        imageFocusArea={imageFocusArea}
        description={description}
        slim={slim ?? false}
        actions={actions}
        onCtaClick={promoClickTrackingEvent}
      />
    </TrackPromotionImpression>
  );
};

export default Hero;
